import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import PropTypes from 'prop-types';

const VideoPlayer = forwardRef(function VideoPlayer(props, ref) {
  const {
    sources,
    onMetadata,
    onPause,
    onPlay,
    onTimeUpdate,
    ...videoJsProps
  } = props;

  const videoNodeRef = useRef(null);
  const playerRef = useRef(null);

  const callbacksRef = useRef({ onMetadata, onPause, onPlay, onTimeUpdate });
  useEffect(() => {
    callbacksRef.current = { onMetadata, onPause, onPlay, onTimeUpdate };
  }, [onMetadata, onPause, onPlay, onTimeUpdate]);

  useImperativeHandle(
    ref,
    () => ({
      player: playerRef.current,
      pause: () => {
        if (playerRef.current) playerRef.current.pause();
      },
      isPlaying: () => {
        if (!playerRef.current) return false;
        return !playerRef.current.paused();
      },
      isFullscreen: () => {
        if (!playerRef.current) return false;
        return playerRef.current.isFullscreen();
      },
      exitFullscreen: () => {
        if (playerRef.current) playerRef.current.exitFullscreen();
      }
    }),
    []
  );

  // Create / dispose player
  useEffect(() => {
    if (!videoNodeRef.current) return;
    if (!window.videojs) return;

    const player = window.videojs(videoNodeRef.current, videoJsProps, () => {
      // ready
    });

    playerRef.current = player;

    const onTimeUpdateHandler = function() {
      const cb = callbacksRef.current.onTimeUpdate;
      if (cb) cb(this.currentTime());
    };

    player.on('timeupdate', onTimeUpdateHandler);

    player.on('loadedmetadata', () => {
      const cb = callbacksRef.current.onMetadata;
      if (cb) cb({ duration: player.duration() });
    });

    player.on('play', () => {
      const cb = callbacksRef.current.onPlay;
      if (cb) cb();
    });

    player.on('pause', () => {
      const cb = callbacksRef.current.onPause;
      if (cb) cb();
    });

    return () => {
      try {
        player.off('timeupdate', onTimeUpdateHandler);
      } catch (e) {
        // ignore
      }
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update source when it changes
  useEffect(() => {
    if (!playerRef.current) return;
    if (!sources || !sources[0]) return;

    const nextSrc = sources[0].src;
    const current =
      playerRef.current.currentSrc && playerRef.current.currentSrc();

    if (nextSrc && (!current || nextSrc !== current)) {
      playerRef.current.src(sources[0]);
    }
  }, [sources]);

  return (
    <div data-vjs-player>
      <video
        ref={videoNodeRef}
        className="video-js vjs-fluid"
        style={{ maxWidth: '100%' }}
        controls
      />
    </div>
  );
});

VideoPlayer.propTypes = {
  sources: PropTypes.array.isRequired,
  onMetadata: PropTypes.func,
  onPause: PropTypes.func,
  onPlay: PropTypes.func,
  onTimeUpdate: PropTypes.func
};

export default VideoPlayer;
